import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpRight, BellOff, Check, Clock, ListTodo, MessageSquare, RotateCw, ShieldCheck, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { jv, resend, errMsg, SEV, ago } from './api';

const ICON = { open: ArrowUpRight, sms: MessageSquare, task: ListTodo, resend_portal: RotateCw, resend_esign: RotateCw, resend_idv: RotateCw };

const TaskForm = ({ token, insight, busy, onApprove }) => {
  const [team, setTeam] = useState([]);
  const [title, setTitle] = useState(insight.title.slice(0, 200));
  const [who, setWho] = useState('');
  useEffect(() => { jv.team(token).then((t) => { setTeam(t); setWho(t[0]?.id || ''); }).catch(() => {}); }, [token]);
  return (
    <div className="space-y-3" data-testid="jv-task-form">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="jv-task-title" />
      <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={who} onChange={(e) => setWho(e.target.value)} data-testid="jv-task-assignee">
        {team.map((m, i) => <option key={m.id} value={m.id}>{i === 0 ? `Me (${m.name})` : m.name}</option>)}
      </select>
      <Approve busy={busy} label="Approve & assign task" onClick={() => onApprove({ action: 'task', title, assignee_id: who })} testid="jv-approve-task" />
    </div>
  );
};

const SmsForm = ({ insight, busy, onApprove }) => {
  const [msg, setMsg] = useState(insight.sms_draft || '');
  return (
    <div className="space-y-3" data-testid="jv-sms-form">
      <div className="text-xs text-slate-500">To {insight.client} · {insight.sms_to}</div>
      <Textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={640} data-testid="jv-sms-message" />
      <Approve busy={busy} label="Approve & send SMS" onClick={() => onApprove({ action: 'sms', message: msg })} testid="jv-approve-sms" disabled={!msg.trim()} />
    </div>
  );
};

const Approve = ({ busy, label, onClick, testid, disabled }) => (
  <button type="button" disabled={busy || disabled} onClick={onClick} data-testid={testid}
    className="cp-dark inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0E1B4D] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#2347FF] disabled:opacity-50">
    <ShieldCheck className="h-4 w-4" />{busy ? 'Working…' : label}
  </button>
);

const RESEND_COPY = { resend_portal: 'their Client Portal link', resend_esign: 'a signing reminder', resend_idv: 'their ID verification link' };

// Review an insight and act on it. Anything that reaches a client or colleague needs an explicit "Approve".
export const ActionSheet = ({ token, insight, open, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMode(null); }, [insight?.id]);
  if (!insight) return null;
  const sev = SEV[insight.severity] || SEV.later;
  const finish = (msg) => { toast.success(msg); onChanged?.(); onClose(); };
  const run = async (fn, okMsg) => { setBusy(true); try { await fn(); finish(okMsg); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const approve = (body) => run(async () => {
    const r = await jv.approve(token, insight.id, body);
    if (r.status === 'blocked') toast.message('Approved. SMS is held on the preview site and will send on the live app.');
    if (!r.ok) throw new Error('Not sent');
  }, body.action === 'sms' ? 'Reminder approved' : 'Task assigned');
  const doResend = () => run(async () => {
    try { await resend(token, mode, insight.ref || {}); } catch (e) { await jv.record(token, insight.id, { action: mode, ok: false, detail: errMsg(e) }).catch(() => {}); throw e; }
    await jv.record(token, insight.id, { action: mode, ok: true, detail: `Resent ${RESEND_COPY[mode]}` });
  }, 'Resent. Jarvis logged it.');
  const pick = (a) => {
    if (a.kind === 'open') { onClose(); navigate(insight.to || '/'); return; }
    setMode(a.kind);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="jv-action-sheet">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold ring-1 ${sev.chip}`} data-testid="jv-sheet-severity"><i className={`h-1.5 w-1.5 rounded-full ${sev.dot}`} />{sev.label}</span>
            <span className="text-[11px] text-slate-500">Flagged {ago(insight.first_seen_at)}</span>
          </div>
          <DialogTitle className="pt-1 text-lg leading-snug" data-testid="jv-sheet-title">{insight.title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-600" data-testid="jv-sheet-why">{insight.why}</DialogDescription>
        </DialogHeader>
        {insight.advice && <div className="flex gap-2 rounded-xl bg-indigo-50 p-3 text-sm text-indigo-900" data-testid="jv-sheet-advice"><Sparkles className="mt-0.5 h-4 w-4 shrink-0" /><span><b>Jarvis recommends:</b> {insight.advice}</span></div>}
        <div className="grid grid-cols-2 gap-2">
          {(insight.actions || []).map((a) => { const Ic = ICON[a.kind] || Check; return (
            <button key={a.kind} type="button" onClick={() => pick(a)} data-testid={`jv-action-${a.kind}`}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm font-semibold transition-colors ${mode === a.kind ? 'border-[#2347FF] bg-indigo-50 text-[#1E3BD6]' : 'border-slate-200 hover:bg-slate-50'}`}>
              <Ic className="h-4 w-4 shrink-0" /><span className="flex-1">{a.label}</span>{a.approval && <ShieldCheck className="h-3.5 w-3.5 text-slate-400" title="Needs your approval" />}
            </button>); })}
        </div>
        {mode === 'sms' && <SmsForm insight={insight} busy={busy} onApprove={approve} />}
        {mode === 'task' && <TaskForm token={token} insight={insight} busy={busy} onApprove={approve} />}
        {mode?.startsWith('resend_') && (
          <div className="space-y-3" data-testid="jv-resend-confirm">
            <p className="text-sm text-slate-600">This texts {insight.client} {RESEND_COPY[mode]} using the same message as the tool itself.</p>
            <Approve busy={busy} label="Approve & resend" onClick={doResend} testid="jv-approve-resend" />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-xs">
          <button type="button" className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 font-semibold text-slate-600 hover:bg-slate-100" onClick={() => run(() => jv.record(token, insight.id, { action: 'handled', ok: true, detail: 'Marked as handled' }), 'Marked as handled')} data-testid="jv-handled"><Check className="h-3.5 w-3.5" />Already handled</button>
          <button type="button" className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 font-semibold text-slate-600 hover:bg-slate-100" onClick={() => run(() => jv.snooze(token, insight.id), 'Snoozed until the next working morning')} data-testid="jv-snooze"><Clock className="h-3.5 w-3.5" />Snooze</button>
          <button type="button" className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 font-semibold text-slate-600 hover:bg-slate-100" onClick={() => run(() => jv.dismiss(token, insight.id), "Dismissed. Jarvis won't raise this again unless it changes.")} data-testid="jv-dismiss"><BellOff className="h-3.5 w-3.5" />Not relevant</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActionSheet;
